import React from "react";
import { Document, Image, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles } from "../../Services/ReactPDFService.jsx";

// Photo Classic Cover Letter Template - Clean design with profile photo matching the resume
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 45,
  },
  // Header Section with photo
  headerContainer: {
    flexDirection: "row",
    marginBottom: 30,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  profilePhotoContainer: {
    width: 70,
    height: 70,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    marginRight: 20,
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  profilePlaceholder: {
    width: 70,
    height: 70,
    borderRadius: 6,
    backgroundColor: "#e0e0e0",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 20,
  },
  profileInitials: {
    fontSize: 22,
    fontWeight: 700,
    color: "#666666",
  },
  headerRight: {
    flex: 1,
    justifyContent: "center",
  },
  name: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  contactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 15,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  contactIcon: {
    width: 10,
    height: 10,
    marginRight: 5,
  },
  contactText: {
    fontSize: 10,
    color: "#444444",
  },
  // Body content
  bodyText: {
    fontSize: 11,
    lineHeight: 1.8,
    color: "#333333",
    textAlign: "justify",
    marginBottom: 16,
  },
});

// Contact icon paths
const contactIcons = {
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  email: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
};

const PhotoClassicCoverLetter = ({ coverLetterData, resumeData, customStyles = {} }) => {
  const { header, generatedContent, profilePhoto } = coverLetterData || {};
  const { name, email, phone, location } = header || {};

  // Get profile photo URL - check multiple locations
  const profilePhotoUrl = profilePhoto?.url || resumeData?.profilePhotoUrl || resumeData?.profilePhoto?.url || null;

  // Get initials for placeholder
  const getInitials = () => {
    if (!name) return "?";
    const parts = name.split(" ");
    const first = parts[0]?.charAt(0) || "";
    const last = parts[parts.length - 1]?.charAt(0) || "";
    return (first + last).toUpperCase();
  };

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { titleColor } = customizations;
    const customStyleOverrides = {};

    if (titleColor) {
      customStyleOverrides.name = {
        ...baseStyles.name,
        color: titleColor,
      };
      customStyleOverrides.headerContainer = {
        ...baseStyles.headerContainer,
        borderBottomColor: titleColor,
      };
    }

    return { ...baseStyles, ...customStyleOverrides };
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Parse the generated content into paragraphs
  const paragraphs = generatedContent
    ? generatedContent.split("\n").filter((p) => p.trim().length > 0)
    : ["[Your cover letter content will appear here]"];

  // Render contact icon
  const renderContactIcon = (iconPath) => (
    <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
      <Path d={iconPath} fill="#444444" />
    </Svg>
  );

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page}>
        {/* Header Section */}
        <View style={mergedStyles.headerContainer}>
          {profilePhotoUrl ? (
            <View style={mergedStyles.profilePhotoContainer}>
              <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
            </View>
          ) : (
            <View style={mergedStyles.profilePlaceholder}>
              <Text style={mergedStyles.profileInitials}>{getInitials()}</Text>
            </View>
          )}
          <View style={mergedStyles.headerRight}>
            <Text style={mergedStyles.name}>{name || "[Your Name]"}</Text>
            <View style={mergedStyles.contactRow}>
              {phone && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.phone)}
                  <Text style={mergedStyles.contactText}>{phone}</Text>
                </View>
              )}
              {email && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.email)}
                  <Text style={mergedStyles.contactText}>{email}</Text>
                </View>
              )}
              {location && (
                <View style={mergedStyles.contactItem}>
                  {renderContactIcon(contactIcons.location)}
                  <Text style={mergedStyles.contactText}>{location}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Body Content */}
        {paragraphs.map((paragraph, index) => (
          <Text key={index} style={mergedStyles.bodyText}>
            {paragraph}
          </Text>
        ))}
      </Page>
    </Document>
  );
};

export default PhotoClassicCoverLetter;
